import React, { useEffect, useState } from "react";
import { LLMCall } from "../API";
import { Box, Button, Grid, Paper, Typography, useTheme } from "@mui/material";
import { useUser } from "../contexts/useUser";
import BlockIcon from "@mui/icons-material/Block";
import { useQueryClient } from "@tanstack/react-query";
import { API } from "aws-amplify";
import { updateLLMCustomerStatus } from "../dataaccess/updateCustomerStatus";
import { downloadS3Content } from "../dataaccess/downloadS3Content";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";

interface EvaluateBlockedPromptProps {
  call?: LLMCall | null;
}

const EvaluateBlockedPrompt: React.FC<EvaluateBlockedPromptProps> = ({
  call,
}) => {
  const theme = useTheme();
  const { project } = useUser();
  const [isPromptCovered, setIsPromptCovered] = useState(true);

  const toxicity = call?.toxicity ? JSON.parse(call?.toxicity) : null;

  useEffect(() => {
    if (toxicity && toxicity.Toxicity && toxicity.Toxicity > 0.5)
      setIsPromptCovered(true);
    else setIsPromptCovered(false);
  }, [call]);

  function cleanToxicityName(label: string): string {
    return label
      .replaceAll("_", " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase());
  }

  function isEmptyObject(obj: object): boolean {
    return Object.keys(obj).length === 0;
  }

  const handleDownload = async (fieldType: string) => {
    if (!call?.id) return;

    try {
      await downloadS3Content(call.id, fieldType);
    } catch (error) {
      console.error(`Failed to download ${fieldType}:`, error);
      // You could add a toast notification here
    }
  };

  const renderS3Content = (
    content: string | undefined,
    fieldType: string,
    label: string
  ) => {
    if (!content) return null;

    if (content.startsWith("s3://")) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body2"
            color="info.main"
            sx={{ wordBreak: "break-all" }}
          >
            <LinkIcon
              sx={{
                fontSize: 16,
                mr: 0.5,
                verticalAlign: "middle",
              }}
            />
            {content}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(fieldType)}
            sx={{ minWidth: "auto", flexShrink: 0 }}
          >
            Download {label}
          </Button>
        </Box>
      );
    }

    return content;
  };

  return (
    <>
      <Box margin={2} maxWidth="100%">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">
              Blocked Prompt - select actions based on prompt
            </Typography>
            <Typography variant="body2">
              Customer: {call?.customer?.name} made on{" "}
              {new Date(call?.createdAt ?? "").toLocaleString()}
            </Typography>
            <Typography variant="body2">
              Location: {call?.country ?? "unknown"},{" "}
              {call?.region ?? "unknown"}
            </Typography>

            {toxicity && !isEmptyObject(toxicity) && (
              <Typography variant="body2">
                Toxicity Total:{" "}
                {toxicity.Toxicity ? toxicity.Toxicity.toFixed(2) : "unknown"}{" "}
                out of 1
              </Typography>
            )}

            {(toxicity && !isEmptyObject(toxicity)) &&
              toxicity.Labels.sort((a: any, b: any) => b.Score - a.Score).map(
                (entry: any) => (
                  <Box key={entry.Name} mb={1}>
                    <Typography variant="body2">
                      {cleanToxicityName(entry.Name)}: {entry.Score.toFixed(2)}
                    </Typography>
                    <Box sx={{ width: "250px" }}>
                      <Box
                        sx={{
                          width: "100%",
                          height: 10,
                          backgroundColor: "lightgrey",
                          position: "relative",
                          borderRadius: 4,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${entry.Score * 100}%`,
                            height: "100%",
                            backgroundColor: "secondary.main",
                            position: "absolute",
                            top: 0,
                            left: 0,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )
              )}

            <Box p={2}>
              <Button
                variant="contained"
                color={
                  call?.customer?.status === "active" ? "secondary" : "primary"
                }
                startIcon={<BlockIcon />}
                sx={{ marginRight: 2 }}
                onClick={async () => {
                  if (call) {
                    await updateLLMCustomerStatus(
                      call?.customerId ?? "",
                      call?.customer?.status === "active"
                        ? "disabled"
                        : "active"
                    );
                    //queryClient.invalidateQueries();
                  }
                }}
              >
                {call?.customer?.status === "active"
                  ? "Block Customer"
                  : "Unblock Customer"}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} lg={6} sx={{ p: 2 }}>
            <Paper
              sx={{ backgroundColor: theme.palette.background.default, p: 2 }}
            >
              <Typography variant="h6">Prompt</Typography>
              {call?.prompt?.startsWith("s3://") ? (
                renderS3Content(call.prompt, "prompt", "Prompt")
              ) : isPromptCovered ? (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setIsPromptCovered(false)}
                >
                  Prompt contains toxic language, click to see
                </Button>
              ) : (
                <Typography variant="body2">{call?.prompt}</Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Paper
              sx={{ backgroundColor: theme.palette.background.default, p: 2 }}
            >
              <Typography variant="h6">Response</Typography>
              {call?.response?.startsWith("s3://") ? (
                renderS3Content(call.response, "response", "Response")
              ) : (
                <Typography variant="body2">{call?.response}</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default EvaluateBlockedPrompt;
